import { DataSourcesContextValue } from '@qfa/forms';
import { useCallback, useMemo } from 'react';
import { useActionSetsDataSource } from './useActionSetsDataSource';
import { useDatabaseEditorTreeDataSource } from './useDatabaseEditorTreeDataSource';
import { useLocationsDataSource } from './useLocationsDataSource';
import { useSignDataSource } from './useSignDataSource';
import { useUsersDataSource } from './useUsersDataSource';

import { useAlarmSignalLocationsTree } from './useAlarmSignalLocationsTree';
import { useAlertNamesDataSource } from './useAlertNamesDataSource';
import { useCameraNamesDataSource } from './useCameraNamesDataSource';
import { useCameraPresetsDataSource } from './useCameraPresetsDataSource';
import { useVideoToursDataSource } from './useCameraToursDataSource';
import { useDetectorGroupLocationTreeDataSource } from './useDetectorGroupLocationTreeDataSource';
import { useGateControllersDataSource } from './useGateControllersDataSource';
import { useMainMapEntityListLocationTreeDataSource } from './useMainMapEntityListLocationTreeDataSource';
import { useMultiDatabaseExportTreeDataSource } from './useMultiDatabaseExportTreeDataSource';
import { useSignNamesDataSource } from './useSignNamesDataSource';
import { useSiteNamesDataSource } from './useSiteNamesDataSource';
import { useTollingAlarmEntityDataSource } from './useTollingAlarmEntityDataSource';
import { useTollingPointNamesDataSource } from './useTollingPointNamesDataSource';
import { useTravelTimeNamesDataSource } from './useTravelTimeNamesDataSource';
import { useTunnelFanNamesDataSource } from './useTunnelFanNamesDataSource';

type UseDataSourcesProps = { integrations?: string[]; values?: any };
export const useDataSources = ({
  integrations,
  values,
}: UseDataSourcesProps): DataSourcesContextValue => {
  const signs = useSignDataSource({ integrations });
  const signNames = useSignNamesDataSource();
  const travelTimeNames = useTravelTimeNamesDataSource();
  const tollingPointNames = useTollingPointNamesDataSource();
  const cameraNames = useCameraNamesDataSource();
  const actionSets = useActionSetsDataSource();
  const users = useUsersDataSource();
  const locations = useLocationsDataSource();
  const alarmIntersectionsTree = useAlarmSignalLocationsTree();
  const alertNames = useAlertNamesDataSource();
  const siteNames = useSiteNamesDataSource();
  const tunnelFanNames = useTunnelFanNamesDataSource();
  const tollingAlarmEntityNames = useTollingAlarmEntityDataSource();

  const databaseEditorTree = useDatabaseEditorTreeDataSource();
  const databaseExportIntersectionTree = useMultiDatabaseExportTreeDataSource();

  const mainMapEntityListSignalTree = useMainMapEntityListLocationTreeDataSource();
  const detectorGroupLocationTree = useDetectorGroupLocationTreeDataSource();

  const gateControllers = useGateControllersDataSource();

  const cameraPresets = useCameraPresetsDataSource();
  const videoTours = useVideoToursDataSource();

  /** this type must be updated in typings.d.ts */
  const dataSources = useMemo(
    () => ({
      actionSets,
      alarmIntersectionsTree,
      alertNames,
      cameraNames,
      cameraPresets,
      databaseEditorTree,
      databaseExportIntersectionTree,
      detectorGroupLocationTree,
      gateControllers,
      locations,
      mainMapEntityListSignalTree,
      signNames,
      signs,
      siteNames,
      tunnelFanNames,
      tollingAlarmEntityNames,
      tollingPointNames,
      travelTimeNames,
      users,
      videoTours,
    }),
    [
      actionSets,
      alarmIntersectionsTree,
      alertNames,
      cameraNames,
      cameraPresets,
      databaseEditorTree,
      databaseExportIntersectionTree,
      detectorGroupLocationTree,
      gateControllers,
      locations,
      mainMapEntityListSignalTree,
      signNames,
      signs,
      siteNames,
      tunnelFanNames,
      tollingAlarmEntityNames,
      tollingPointNames,
      travelTimeNames,
      users,
      videoTours,
    ]
  );

  const getSource = useCallback(
    (name: keyof typeof dataSources) => {
      return dataSources[name];
    },
    [dataSources]
  );

  return useMemo(
    () =>
      ({
        getSource,
        values,
      } as DataSourcesContextValue),
    [getSource, values]
  );
};
